/**
 * Legacy Quorum Service
 * Handles API communications for legacy Quorum film tracking data
 */

import apiService from './apiService';

class LegacyQuorumService {
  constructor() {
    this.baseEndpoint = '/api/v1/legacy-quorum';
  }

  /**
   * Get paginated legacy Quorum films with filtering and sorting
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Records per page (default: 20, max: 100)
   * @param {string} params.film_name - Filter by film name (partial match)
   * @param {string} params.imdb_id - Filter by IMDB ID
   * @param {number} params.release_year - Filter by release year
   * @param {number} params.min_awareness - Minimum average awareness
   * @param {number} params.max_awareness - Maximum average awareness
   * @param {number} params.min_interest - Minimum average interest
   * @param {number} params.max_interest - Maximum average interest
   * @param {string} params.sort_by - Sort field (film_name, release_year, latest_awareness, latest_interest, avg_awareness, avg_interest, survey_count)
   * @param {string} params.sort_order - Sort direction (asc, desc)
   * @returns {Promise<Object>} - API response with films data
   */
  async getFilms(params = {}) {
    try {
      console.log('📊 Fetching legacy Quorum films with pagination...', params);
      
      const queryParams = new URLSearchParams();
      
      // Pagination parameters
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Filtering parameters
      if (params.film_name) queryParams.append('film_name', params.film_name);
      if (params.imdb_id) queryParams.append('imdb_id', params.imdb_id);
      if (params.release_year) queryParams.append('release_year', params.release_year);
      if (params.min_awareness) queryParams.append('min_awareness', params.min_awareness);
      if (params.max_awareness) queryParams.append('max_awareness', params.max_awareness);
      if (params.min_interest) queryParams.append('min_interest', params.min_interest);
      if (params.max_interest) queryParams.append('max_interest', params.max_interest);
      
      // Sorting parameters
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      
      const endpoint = `${this.baseEndpoint}/films`;
      
      // Convert URLSearchParams to object for apiService
      const paramsObject = {};
      queryParams.forEach((value, key) => {
        paramsObject[key] = value;
      });
      
      const response = await apiService.get(endpoint, paramsObject);
      
      console.log('✅ Legacy Quorum films fetched successfully:', response);
      return response;
      
    } catch (error) {
      console.error('❌ Failed to fetch legacy Quorum films:', error.message);
      throw error;
    }
  }

  /**
   * Get detailed film information with surveys and demographics
   * @param {string} filmId - Film ID to fetch details for
   * @returns {Promise<Object>} - Film details with surveys and demographics
   */
  async getFilmDetails(filmId) {
    try {
      console.log('🎬 Fetching legacy Quorum film details for:', filmId);
      
      const endpoint = `${this.baseEndpoint}/films/${filmId}`;
      
      // Pass query parameters separately to avoid URL construction issues
      const params = {
        include_surveys: true,
        include_demographics: true
      };
      
      const response = await apiService.get(endpoint, params);
      
      console.log('✅ Legacy Quorum film details fetched successfully:', response);
      return response;
      
    } catch (error) {
      console.error('❌ Failed to fetch legacy Quorum film details:', error.message);
      throw error;
    }
  }

  /**
   * Get IMDB metadata for a film
   * @param {string} imdbId - IMDB ID of the film
   * @returns {Promise<Object>} - IMDB film metadata or null if not available
   */
  async getIMDBMetadata(imdbId) {
    try {
      console.log('🎬 Fetching IMDB metadata for:', imdbId);
      
      const response = await apiService.get(`/api/v1/imdb/${imdbId}`);
      
      console.log('✅ IMDB metadata fetched successfully:', response);
      return response;
      
    } catch (error) {
      // Handle 404 and other errors gracefully
      if (error.message.includes('API endpoint not found') || error.type === 'notFound') {
        console.warn('⚠️ IMDB metadata not found for:', imdbId);
        return { success: false, data: null, error: 'IMDB data not available' };
      } else {
        console.error('❌ Failed to fetch IMDB metadata:', error.message);
        return { success: false, data: null, error: error.message };
      }
    }
  }

  /**
   * Get legacy Quorum dashboard statistics
   * Since there's no dedicated stats endpoint, we'll calculate from films data
   * @returns {Promise<Object>} - Dashboard stats for legacy data
   */
  async getDashboardStats() {
    try {
      console.log('📊 Calculating legacy Quorum dashboard stats from films data...');
      
      // Get a sample of films to calculate stats
      const response = await this.getFilms({ limit: 100 });
      
      if (!response.success || !response.data.films) {
        throw new Error('No films data available');
      }
      
      const films = response.data.films;
      const pagination = response.data.pagination;
      
      // Calculate stats from the films data
      const stats = this.calculateStatsFromFilms(films, pagination);
      
      console.log('✅ Legacy Quorum dashboard stats calculated:', stats);
      return {
        success: true,
        data: stats,
        timestamp: response.timestamp
      };
      
    } catch (error) {
      console.error('❌ Failed to calculate legacy Quorum dashboard stats:', error.message);
      
      // Return fallback stats if API fails
      return {
        success: false,
        data: {
          totalFilms: 0,
          dateRange: 'N/A',
          lastUpdated: 'N/A',
          dataQuality: 'Unknown',
          averageRating: 0,
          totalBoxOffice: 0,
          topGenre: 'Unknown'
        },
        error: error.message
      };
    }
  }

  /**
   * Calculate dashboard statistics from films data
   * @param {Array} films - Array of film objects
   * @param {Object} pagination - Pagination info
   * @returns {Object} - Calculated statistics
   */
  calculateStatsFromFilms(films, pagination) {
    if (!films || films.length === 0) {
      return {
        totalFilms: 0,
        dateRange: 'N/A',
        lastUpdated: 'N/A',
        dataQuality: 'Unknown',
        averageRating: 0,
        totalBoxOffice: 0,
        topGenre: 'Unknown'
      };
    }

    // Calculate date range
    const years = films.map(f => f.release_year).filter(y => y);
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const dateRange = years.length > 0 ? `${minYear}-${maxYear}` : 'N/A';

    // Calculate average metrics
    const validFilms = films.filter(f => f.avg_awareness !== null);
    const avgAwareness = validFilms.length > 0 
      ? validFilms.reduce((sum, f) => sum + f.avg_awareness, 0) / validFilms.length 
      : 0;
    
    const avgInterest = validFilms.length > 0 
      ? validFilms.reduce((sum, f) => sum + (f.avg_interest || 0), 0) / validFilms.length 
      : 0;

    // Calculate data quality based on completeness
    const totalFields = films.length * 4; // awareness, interest, location, paytosee
    const filledFields = films.reduce((sum, f) => {
      return sum + (f.avg_awareness !== null ? 1 : 0) + 
                   (f.avg_interest !== null ? 1 : 0) + 
                   (f.avg_location !== null ? 1 : 0) + 
                   (f.avg_paytosee !== null ? 1 : 0);
    }, 0);
    
    const dataQuality = totalFields > 0 ? (filledFields / totalFields * 100).toFixed(1) + '%' : 'Unknown';

    return {
      totalFilms: pagination.total || films.length,
      dateRange: dateRange,
      lastUpdated: new Date().toISOString(),
      dataQuality: dataQuality,
      averageAwareness: avgAwareness.toFixed(1),
      averageInterest: avgInterest.toFixed(1),
      totalSurveys: films.reduce((sum, f) => sum + (f.survey_count || 0), 0),
      avgSurveysPerFilm: (films.reduce((sum, f) => sum + (f.survey_count || 0), 0) / films.length).toFixed(1)
    };
  }


  /**
   * Transform legacy film data for UI consumption
   * @param {Object} rawFilm - Raw film data from API
   * @returns {Object} - Transformed film data
   */
  transformFilmData(rawFilm) {
    // Format release date
    const releaseDate = rawFilm.release_year && rawFilm.release_month && rawFilm.release_day
      ? new Date(rawFilm.release_year, rawFilm.release_month - 1, rawFilm.release_day)
      : null;

    return {
      id: rawFilm.film_id || 'unknown',
      title: rawFilm.film_name || 'Unknown Title',
      releaseDate: releaseDate,
      releaseYear: rawFilm.release_year || null,
      imdbId: rawFilm.imdb_id || null,
      
      // Survey metrics
      surveyCount: rawFilm.survey_count || 0,
      firstSurveyDate: rawFilm.first_survey_date || null,
      lastSurveyDate: rawFilm.last_survey_date || null,
      
      // Latest metrics
      latestAwareness: rawFilm.latest_awareness || null,
      latestInterest: rawFilm.latest_interest || null,
      latestLocation: rawFilm.latest_location || null,
      latestPayToSee: rawFilm.latest_paytosee || null,
      
      // Average metrics
      avgAwareness: rawFilm.avg_awareness || null,
      avgInterest: rawFilm.avg_interest || null,
      avgLocation: rawFilm.avg_location || null,
      avgPayToSee: rawFilm.avg_paytosee || null,
      
      // Volatility metrics
      awarenessVolatility: rawFilm.awareness_volatility || null,
      interestVolatility: rawFilm.interest_volatility || null,
      
      // Legacy data flags
      legacyData: true,
      dataSource: 'Legacy Quorum',
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Transform dashboard stats for UI consumption
   * @param {Object} rawStats - Raw stats from API
   * @returns {Object} - Transformed stats
   */
  transformDashboardStats(rawStats) {
    if (!rawStats.success || !rawStats.data) {
      return {
        totalFilms: 0,
        dateRange: 'N/A',
        lastUpdated: 'N/A',
        dataQuality: 'Unknown',
        averageRating: 0,
        totalBoxOffice: 0,
        topGenre: 'Unknown'
      };
    }

    const data = rawStats.data;
    return {
      totalFilms: data.total_films || data.totalFilms || 0,
      dateRange: data.date_range || data.dateRange || 'N/A',
      lastUpdated: data.last_updated || data.lastUpdated || 'N/A',
      dataQuality: data.data_quality || data.dataQuality || 'Good',
      averageRating: data.average_rating || data.averageRating || 0,
      totalBoxOffice: data.total_box_office || data.totalBoxOffice || 0,
      topGenre: data.top_genre || data.topGenre || 'Unknown'
    };
  }
}

// Create and export singleton instance
const legacyQuorumService = new LegacyQuorumService();
export default legacyQuorumService;
